import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { authApi, inr } from '@/lib/erpApi';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Store, Search } from 'lucide-react';

const CATEGORIES = ['Room', 'Shop', 'Office', 'Warehouse', 'Apartment', 'Commercial Unit', 'Hostel', 'PG', 'Hall', 'Godown', 'Other'];
const STATUSES = ['Vacant', 'Occupied', 'Reserved', 'Maintenance'];

const statusColor = {
  Vacant: 'bg-emerald-100 text-emerald-800',
  Occupied: 'bg-sky-100 text-sky-800',
  Reserved: 'bg-amber-100 text-amber-800',
  Maintenance: 'bg-rose-100 text-rose-800',
};

const blank = {
  building_id: '', floor: 0, unit_number: '', category: 'Room',
  monthly_rent: 0, security_deposit: 0, maintenance_charges: 0, water_charges: 0, other_charges: 0,
  electric_meter_number: '', electricity_rate: 0, electricity_fixed_charges: 0,
  carpet_area_sqft: '', builtup_area_sqft: '', facing: '', furnishing: '',
  status: 'Vacant', remarks: '',
};

export default function UnitsPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [buildings, setBuildings] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ building_id: 'all', status: 'all', category: 'all', search: '' });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([
        api.get('/erp/buildings'),
        api.get('/erp/units', {
          params: {
            ...(filter.building_id !== 'all' && { building_id: filter.building_id }),
            ...(filter.status !== 'all' && { status: filter.status }),
            ...(filter.category !== 'all' && { category: filter.category }),
            ...(filter.search && { search: filter.search }),
          },
        }),
      ]);
      setBuildings(b.data);
      setRows(u.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const buildingMap = useMemo(() => Object.fromEntries(buildings.map(b => [b.id, b.name])), [buildings]);

  const openNew = () => {
    if (!buildings.length) return toast.error('Create a building first');
    setEditing(null);
    setForm({ ...blank, building_id: buildings[0].id });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...blank, ...u, carpet_area_sqft: u.carpet_area_sqft ?? '', builtup_area_sqft: u.builtup_area_sqft ?? '' });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.building_id || !form.unit_number) return toast.error('Building and unit number required');
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numeric-empty strings
      ['carpet_area_sqft', 'builtup_area_sqft'].forEach((k) => {
        payload[k] = payload[k] === '' ? null : Number(payload[k]);
      });
      ['floor', 'monthly_rent', 'security_deposit', 'maintenance_charges', 'water_charges',
        'other_charges', 'electricity_rate', 'electricity_fixed_charges'].forEach((k) => {
        payload[k] = Number(payload[k] || 0);
      });
      if (editing) {
        await api.put(`/erp/units/${editing.id}`, payload);
        toast.success('Unit updated');
      } else {
        await api.post('/erp/units', payload);
        toast.success('Unit created');
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete unit "${u.unit_number}"?`)) return;
    try {
      await api.delete(`/erp/units/${u.id}`);
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="erp-units-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Rooms & Shops</h1>
          <p className="text-gray-500 text-sm mt-1">All rental units (rooms, shops, offices, custom categories).</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-unit-btn">
          <Plus className="h-4 w-4 mr-2" /> New Unit
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search unit/meter/remarks" className="pl-9" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
          </div>
          <Select value={filter.building_id} onValueChange={(v) => setFilter({ ...filter, building_id: v })}>
            <SelectTrigger><SelectValue placeholder="All buildings" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buildings</SelectItem>
              {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
            <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No units match the current filters.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="p-3">Unit</th>
                  <th className="p-3">Building · Floor</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Rent</th>
                  <th className="p-3">Deposit</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-t hover:bg-gray-50" data-testid={`unit-row-${u.unit_number}`}>
                    <td className="p-3 font-medium text-gray-900 flex items-center gap-2">
                      <Store className="h-4 w-4 text-brand-orange" /> {u.unit_number}
                    </td>
                    <td className="p-3 text-gray-600">{buildingMap[u.building_id] || '—'} · Floor {u.floor}</td>
                    <td className="p-3">{u.category}</td>
                    <td className="p-3 font-mono">{inr(u.monthly_rent)}</td>
                    <td className="p-3 font-mono">{inr(u.security_deposit)}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${statusColor[u.status] || 'bg-gray-100'}`}>{u.status}</span></td>
                    <td className="p-3 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="unit-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Unit' : 'New Unit'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1 md:col-span-2">
                <Label>Building *</Label>
                <Select value={form.building_id} onValueChange={(v) => setForm({ ...form, building_id: v })}>
                  <SelectTrigger data-testid="unit-building"><SelectValue placeholder="Select building" /></SelectTrigger>
                  <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Floor</Label>
                <Input type="number" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Unit Number *</Label>
                <Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} required data-testid="unit-number" />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-1"><Label>Monthly Rent (₹)</Label><Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} /></div>
              <div className="space-y-1"><Label>Security Deposit (₹)</Label><Input type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })} /></div>
              <div className="space-y-1"><Label>Maintenance (₹)</Label><Input type="number" value={form.maintenance_charges} onChange={(e) => setForm({ ...form, maintenance_charges: e.target.value })} /></div>
              <div className="space-y-1"><Label>Water Charges (₹)</Label><Input type="number" value={form.water_charges} onChange={(e) => setForm({ ...form, water_charges: e.target.value })} /></div>
              <div className="space-y-1"><Label>Other Charges (₹)</Label><Input type="number" value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} /></div>

              <div className="space-y-1 md:col-span-3 pt-2 border-t"><Label className="text-xs uppercase tracking-wider text-gray-500">Electricity</Label></div>
              <div className="space-y-1"><Label>Meter Number</Label><Input value={form.electric_meter_number || ''} onChange={(e) => setForm({ ...form, electric_meter_number: e.target.value })} /></div>
              <div className="space-y-1"><Label>Rate per Unit (₹)</Label><Input type="number" step="0.01" value={form.electricity_rate} onChange={(e) => setForm({ ...form, electricity_rate: e.target.value })} /></div>
              <div className="space-y-1"><Label>Fixed Charges (₹)</Label><Input type="number" value={form.electricity_fixed_charges} onChange={(e) => setForm({ ...form, electricity_fixed_charges: e.target.value })} /></div>

              <div className="space-y-1 md:col-span-3 pt-2 border-t"><Label className="text-xs uppercase tracking-wider text-gray-500">Physical</Label></div>
              <div className="space-y-1"><Label>Carpet Area (sqft)</Label><Input type="number" value={form.carpet_area_sqft} onChange={(e) => setForm({ ...form, carpet_area_sqft: e.target.value })} /></div>
              <div className="space-y-1"><Label>Built-up Area (sqft)</Label><Input type="number" value={form.builtup_area_sqft} onChange={(e) => setForm({ ...form, builtup_area_sqft: e.target.value })} /></div>
              <div className="space-y-1"><Label>Facing</Label><Input value={form.facing || ''} onChange={(e) => setForm({ ...form, facing: e.target.value })} /></div>
              <div className="space-y-1"><Label>Furnishing</Label><Input value={form.furnishing || ''} onChange={(e) => setForm({ ...form, furnishing: e.target.value })} placeholder="Unfurnished / Semi / Fully" /></div>

              <div className="space-y-1 md:col-span-3">
                <Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
